import { all, call, put, takeLatest, select, delay } from 'redux-saga/effects';
import { fetchClient } from '../utilities/fetchClient';
import ActionTypes from '../actions/actionTypes';
import {
  downloadUsersSuccess,
  downloadUsersFailure,
  downloadAllUsersSuccess,
  downloadAllUsersFailure,
  getAssignedPortalAccountsListSuccess,
  getAssignedPortalAccountsListFailure,
  removeAccountAccessForUserSuccess,
  removeAccountAccessForUserFailure,
  setCanReadWrite,
  setPortalType,
  setUserAsAmpolAdmin,
  setAssignedGrandparentAccount,
  setUserAsUnauthorised,
  removeAccountAccessForUserReset,
  assignAccountsToUserReset,
  adminUpdateUserReset,
  updateUserReset,
  createUserReset,
} from '../actions/userActions';
import Env from '../Env';
import fileDownload from 'js-file-download';
import { isMobile } from '../utilities/usersDevice';
import { usersFileName } from '../constants';
import { getAllAccountsRequest } from 'actions/accountActions';
import { isAmpolAdmin, isAuthorised, isCustomer, userActionTypes } from 'utilities/user-utilities';
import { getPortalType } from 'utilities/core-utilities';
import { authProvider } from 'authProvider';
import { addToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';

function* getUsers(action) {
  const {
    clientId,
    ...queryParams
  } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: `users-api/v1/${clientId}/users/search`,
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
      queryParams
    });

    yield put({
      type: ActionTypes.USERS.FETCH.SUCCESS,
      payload: {
        users: response.result,
        totalSize: response.resultSize,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.FETCH.FAILURE, payload: e });
  }
}

function* loginUser() {
  try {
    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };
    const response = yield call(fetchClient, {
      url: 'users-api/v1/users/login',
      method: 'POST',
      headers: header,
      apiServer: Env.getApimBaseUrl()
    });
    yield put({
      type: ActionTypes.USERS.LOGIN_USER.SUCCESS,
      payload: {
        user: response,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.LOGIN_USER.FAILURE, payload: e });
  }
}

function* getUserDetails() {
  try {
    const header = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };
    const response = yield call(fetchClient, {
      url: 'users-api/v2/users/ME',
      method: 'GET',
      headers: header,
      apiServer: Env.getApimBaseUrl()
    });
    yield put({
      type: ActionTypes.USERS.ME.SUCCESS,
      payload: {
        user: response,
      },
    });

    const isAdmin = isAmpolAdmin(response);
    const isAdminPortal = Env.isAdminPortal();
    const isCustomerUser = isCustomer(response);

    let assignedAccount = undefined;
    if (isAdmin) {
      yield put(setUserAsAmpolAdmin());
    } else if (isCustomerUser && response?.accounts?.length > 0) {
      if (Env.isAmpolCashEnabled()) {
        assignedAccount = response?.accounts?.[0];
      } else {
        assignedAccount = response?.accounts?.find((acc) => getPortalType(acc.grandParentAccountNumber) !== 'cash');
      }
  
      if (!isAdminPortal && !isAdmin)
        yield put(setAssignedGrandparentAccount(assignedAccount));
    }

    const grandParent = !isAdmin ? assignedAccount?.grandParentAccountNumber : undefined;

    if ((isCustomerUser && !isAdmin && isAdminPortal) || (isAdmin && !isCustomerUser && !isAdminPortal) || (!isAdmin && isCustomerUser && !isAdminPortal && !Env.isAmpolCashEnabled() && grandParent === undefined)) {
      yield put(setUserAsUnauthorised());
    }

    if (isAdminPortal) {
      document.title = 'Ampol | Admin Portal';
    }
  } catch (e) {
    yield put({ type: ActionTypes.USERS.ME.FAILURE, payload: e });
  }
}

function* createUser(action) {
  const { clientId, createUserRequest } = action.payload;
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    yield put(addToastAlert(Alert.ADD_USER_LOADING));
    const response = yield call(fetchClient, {
      url: `users-api/v1/${clientId}/users`,
      method: 'POST',
      body: createUserRequest,
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });
    yield put({
      type: ActionTypes.USERS.CREATE.SUCCESS,
      payload: {
        user: response,
      },
    });
    yield put(addToastAlert(Alert.ADD_USER_SUCCESS, 'success'));
    yield delay();
    yield put(createUserReset());
  } catch (e) {
    yield put({ type: ActionTypes.USERS.CREATE.FAILURE, payload: e });
    yield put(addToastAlert(Alert.ADD_USER_ERROR, 'error'));
    yield put(createUserReset());
  }
}

function* getDetailedUser(action) {
  const {
    userId,
    grandParentCustomerNumber,
  } = action.payload;

  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: `users-api/v1/${grandParentCustomerNumber}/users/${userId}`,
      method: 'GET',
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });
    yield put({
      type: ActionTypes.USERS.DETAILED_FETCH.SUCCESS,
      payload: {
        user: response,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.DETAILED_FETCH.FAILURE, payload: e });
    yield put(addToastAlert(Alert.FETCH_USER_ERROR, 'error'));
  }
}

function* adminGetDetailedUser(action) {
  const {
    userId,
  } = action.payload;

  const headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: `users-api/v1/users/${userId}`,
      method: 'GET',
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });

    yield put({
      type: ActionTypes.USERS.ADMIN_DETAILED_FETCH.SUCCESS,
      payload: {
        user: response,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.ADMIN_DETAILED_FETCH.FAILURE, payload: e });
    yield put(addToastAlert(Alert.FETCH_USER_ERROR, 'error'));
  }
}

function* getUserByEmailId(action) {
  try {
    let headerPayload = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };
    const response = yield call(fetchClient, {
      url: 'users-api/v1/users/email',
      method: 'POST',
      headers: headerPayload,
      body: { emailId: action.payload.emailId },
      apiServer: Env.getApimBaseUrl()
    });
    yield put({
      type: ActionTypes.USERS.EMAIL_LOOKUP.SUCCESS,
      payload: {
        emailId: action.payload.emailId,
        user: response,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.EMAIL_LOOKUP.FAILURE, payload: e });
  }
}

function* updateUser(action) {
  const {
    userId,
    grandParentCustomerNumber,
    updateUserProfileRequest,
  } = action.payload;
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    const response = yield call(fetchClient, {
      url: `users-api/v1/${grandParentCustomerNumber}/users/${userId}`,
      method: 'PUT',
      body: updateUserProfileRequest,
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });

    yield put(addToastAlert(Alert.UPDATE_USER_LOADING));
    yield put({
      type: ActionTypes.USERS.UPDATE.SUCCESS,
      payload: {
        user: response,
      },
    });

    yield put(addToastAlert(Alert.UPDATE_USER_SUCCESS, 'success'));
    yield put(updateUserReset());
  } catch (e) {
    yield put({ type: ActionTypes.USERS.UPDATE.FAILURE, payload: e });

    yield put(addToastAlert(Alert.UPDATE_USER_ERROR, 'error'));
    yield put(updateUserReset());
  }
}

function* adminUpdateUser(action) {
  const {
    userId,
    updateUserProfileRequest,
  } = action.payload;

  const headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };
  try {
    yield put(addToastAlert(Alert.UPDATE_USER_LOADING));
    const response = yield call(fetchClient, {
      url: `users-api/v1/users/${userId}`,
      method: 'PUT',
      body: updateUserProfileRequest,
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });
    yield put({
      type: ActionTypes.USERS.ADMIN_UPDATE_USER.SUCCESS,
      payload: {
        user: response,
      },
    });

    yield put(addToastAlert(Alert.UPDATE_USER_SUCCESS, 'success'));
    yield put(adminUpdateUserReset());
  } catch (e) {
    yield put({ type: ActionTypes.USERS.ADMIN_UPDATE_USER.FAILURE, payload: e });
    yield put(addToastAlert(Alert.UPDATE_USER_ERROR, 'error'));
    yield put(adminUpdateUserReset());
  }
}

function* updateUserSelf(action) {
  const {
    userId,
    grandParentCustomerNumber,
    updateUserProfileRequest,
  } = action.payload;
  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.UPDATE_USER_LOADING));
    const response = yield call(fetchClient, {
      url:  `users-api/v1/${grandParentCustomerNumber}/users/self/${userId}`,
      method: 'PUT',
      body: updateUserProfileRequest,
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });

    yield put({
      type: ActionTypes.USERS.UPDATE_USER_SELF.SUCCESS,
      payload: {
        user: response,
      },
    });
    yield put(addToastAlert(Alert.UPDATE_USER_SUCCESS, 'success'));
  } catch (e) {
    yield put({ type: ActionTypes.USERS.UPDATE_USER_SELF.FAILURE, payload: e });
    yield put(addToastAlert(Alert.UPDATE_USER_ERROR, 'error'));
  }
}

function* deleteUser(action) {
  const {
    clientId,
    userId,
  } = action.payload;

  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.DEACTIVATE_ACCESS_LOADING));
    yield call(fetchClient, {
      url: `users-api/v1/${clientId}/users/${userId}`,
      method: 'DELETE',
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });

    yield put({
      type: ActionTypes.USERS.DELETE.SUCCESS,
    });
    yield put(addToastAlert(Alert.DEACTIVATE_ACCESS_SUCCESS, 'success'));
  } catch (e) {
    yield put({ type: ActionTypes.USERS.DELETE.FAILURE, payload: e });
    yield put(addToastAlert(Alert.DEACTIVATE_ACCESS_ERROR, 'error'));

  }
}

function* adminDeleteUser(action) {
  const {
    userId,
  } = action.payload;

  let headerPayload = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.DEACTIVATE_ACCESS_LOADING));
    yield call(fetchClient, {
      url: `users-api/v1/users/${userId}`,
      method: 'DELETE',
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });

    yield put({
      type: ActionTypes.USERS.ADMIN_DELETE_USER.SUCCESS,
    });
    yield put(addToastAlert(Alert.DEACTIVATE_ACCESS_SUCCESS, 'success'));
  } catch (e) {
    yield put({ type: ActionTypes.USERS.ADMIN_DELETE_USER.FAILURE, payload: e });
    yield put(addToastAlert(Alert.DEACTIVATE_ACCESS_ERROR, 'error'));
  }
}

function* assignAccountsToUser(action) {
  const {
    grandParentCustomerNumber,
    userId,
    assignAccountsUserMap,
    accountDeselection,
    assignedClientRoles,
  } = action.payload;
  
  try {
    const assignAccountsRequest = {
      assignedAccounts: [],
      unassignedaccounts: accountDeselection,
      assignedClientRoles: assignedClientRoles,
    };
    
    let index = 0;
    assignAccountsUserMap?.forEach((value) => {
      assignAccountsRequest.assignedAccounts[index] = value;
      index++;
    });

    const headerPayload = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    yield put(addToastAlert(Alert.ASSIGN_ACCOUNTS_LOADING));
    yield call(fetchClient, {
      url: `users-api/v1/${grandParentCustomerNumber}/users/${userId}/accountassignments`,
      method: 'PUT',
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl(),
      body: JSON.stringify(assignAccountsRequest),
    });
    yield put({
      type: ActionTypes.USERS.ASSIGN_ACCOUNTS.SUCCESS,
    });

    yield put(addToastAlert(Alert.ASSIGN_ACCOUNTS_SUCCESS, 'success'));
    yield put(assignAccountsToUserReset());
  } catch (e) {
    yield put({ type: ActionTypes.USERS.ASSIGN_ACCOUNTS.FAILURE, payload: e });
    yield put(addToastAlert(Alert.ASSIGN_ACCOUNTS_ERROR, 'error'));
    yield put(assignAccountsToUserReset());
  }
}

function* downloadUsers(action) {
  const {
    clientId,
    ...queryParams
  } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.DOWNLOAD_USERS_LOADING));
    const response = yield call(fetchClient, {
      url: `users-api/v1/${clientId}/users/download`,
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
      queryParams,
      responseType: 'blob'
    });

    if (isMobile()) {
      var reader = new FileReader();
      var blobData = new Blob([response], { type: 'application/vnd.ms-excel' });
      reader.onload = function(){
        window.location.href = reader.result;
      };
      reader.readAsDataURL(blobData);
    }
    else {
      yield call(fileDownload, response, usersFileName);
    }
    yield put(downloadUsersSuccess());
    yield put(addToastAlert(Alert.DOWNLOAD_USERS_SUCCESS, 'success'));
  } catch (e) {
    yield put(downloadUsersFailure());
    yield put(addToastAlert(Alert.DOWNLOAD_USERS_ERROR, 'error'));
  }
}

function* downloadAllUsers(action) {
  const {
    ...queryParams
  } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield put(addToastAlert(Alert.DOWNLOAD_USERS_LOADING));
    const response = yield call(fetchClient, {
      url: 'users-api/v1/users/download',
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
      queryParams,
      responseType: 'blob'
    });

    if (isMobile()) {
      var reader = new FileReader();
      var blobData = new Blob([response], { type: 'application/vnd.ms-excel' });
      reader.onload = function(){
        window.location.href = reader.result;
      };
      reader.readAsDataURL(blobData);
    }
    else {
      yield call(fileDownload, response, usersFileName);
    }
    yield put(downloadAllUsersSuccess());
    yield put(addToastAlert(Alert.DOWNLOAD_USERS_SUCCESS, 'success'));
  } catch (e) {
    yield put(downloadAllUsersFailure());
    yield put(addToastAlert(Alert.DOWNLOAD_USERS_ERROR, 'error'));
  }
}

function* getAllUsers(action) {
  const {
    ...queryParams
  } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: 'users-api/v1/users',
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
      queryParams
    });

    yield put({
      type: ActionTypes.USERS.SEARCH_ALL_USERS.SUCCESS,
      payload: {
        users: response.result,
        totalSize: response.resultSize,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.SEARCH_ALL_USERS.FAILURE, payload: e });
  }
}

function* getAllImpersonateUsers(action) {
  const {
    ...queryParams
  } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: 'users-api/v1/users',
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
      queryParams
    });

    yield put({
      type: ActionTypes.USERS.SEARCH_ALL_IMPERSONATE_USERS.SUCCESS,
      payload: {
        users: response.result,
        totalSize: response.resultSize,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.SEARCH_ALL_IMPERSONATE_USERS.FAILURE, payload: e });
  }
}

function* impersonateUser(action) {
  const {
    userId,
  } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield call(fetchClient, {
      url: `users-api/v1/users/${userId}/impersonate`,
      method: 'POST',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
      body: { reason: '' },
    });

    yield put({
      type: ActionTypes.USERS.IMPERSONATE_USER.SUCCESS,
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.IMPERSONATE_USER.FAILURE, payload: e });
  }
}

function* getAssignedPortalAccountsList(action) {
  try {
    const {
      userId,
      ...queryParams
    } = action.payload;

    const headerPayload = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };

    const response = yield call(fetchClient, {
      url: `users-api/v1/users/${userId}/accountassignments`,
      method: 'GET',
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl(),
      queryParams: {
        ...queryParams,
      },
    });
    yield put(getAssignedPortalAccountsListSuccess(response));
  } catch (e) {
    yield put(getAssignedPortalAccountsListFailure());
  }
}

function* removeAccountAccessForUser(action) {
  try {
    const {
      userId,
      accountNumber,
      isLastAccount
    } = action.payload;

    const headerPayload = {
      'Ocp-Apim-Subscription-Key': Env.getApimKey(),
    };
    
    yield put(addToastAlert(Alert.REMOVE_ACCOUNT_ACCESS_LOADING));
    yield call(fetchClient, {
      url: `users-api/v1/users/${userId}/accountassignments/${accountNumber}?grandParentAccountNumber=${action.payload.grandParentAccountNumber}`,
      method: 'DELETE',
      headers: headerPayload,
      apiServer: Env.getApimBaseUrl()
    });
    yield put(removeAccountAccessForUserSuccess(isLastAccount));    
    yield put(addToastAlert(Alert.REMOVE_ACCOUNT_ACCESS_SUCCESS, 'success'));
    yield put(removeAccountAccessForUserReset());
  } catch (e) {
    yield put(removeAccountAccessForUserFailure());    
    yield put(addToastAlert(Alert.REMOVE_ACCOUNT_ACCESS_ERROR, 'error'));
    yield put(removeAccountAccessForUserReset());
  }
}

function* logout() {
  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    yield call(fetchClient, {
      url: 'users-api/v1/users/logout',
      method: 'POST',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
    });

    yield put({
      type: ActionTypes.USERS.LOGOUT.SUCCESS,
    });

    authProvider.logoutRedirect();
  } catch (e) {
    yield put({ type: ActionTypes.USERS.LOGOUT.FAILURE, payload: e });
  }
}

function* searchUserExists(action) {
  const {
    clientId,
    ...queryParams
  } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: `users-api/v1/${clientId}/users/search`,
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
      queryParams
    });

    yield put({
      type: ActionTypes.USERS.SEARCH_USER_EXISTS.SUCCESS,
      payload: {
        users: response.result,
        totalSize: response.resultSize,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.SEARCH_USER_EXISTS.FAILURE, payload: e });
  }
}

function* adminSearchUserExists(action) {
  const {
    ...queryParams
  } = action.payload;

  const header = {
    'Ocp-Apim-Subscription-Key': Env.getApimKey(),
  };

  try {
    const response = yield call(fetchClient, {
      url: 'users-api/v1/users',
      method: 'GET',
      apiServer: Env.getApimBaseUrl(),
      headers: header,
      queryParams
    });

    yield put({
      type: ActionTypes.USERS.ADMIN_SEARCH_USER_EXISTS.SUCCESS,
      payload: {
        users: response.result,
        totalSize: response.resultSize,
      },
    });
  } catch (e) {
    yield put({ type: ActionTypes.USERS.ADMIN_SEARCH_USER_EXISTS.FAILURE, payload: e });
  }
}

function* setAssignedGrandparent(action) {
  const loggedInUser = yield select((state) => state.userStore.loggedInUser);
  const grandfatherClientNumber = action?.payload?.grandParentAccountNumber;
  const accountNumber = action?.payload?.accountNumber;
  const isAdminPortal = Env.isAdminPortal();

  const prevPortalType = yield select((state) => state.userStore.portalType);
  // Admin portal can only be a cash portal
  const newPortalType = isAdminPortal ? 'cash' : getPortalType(grandfatherClientNumber);
  
  sessionStorage.removeItem('report-contractNumber');

  yield put(setCanReadWrite({
    accounts: isAuthorised(loggedInUser, grandfatherClientNumber, [userActionTypes.AccountsReadWrite]),
    cards: isAuthorised(loggedInUser, grandfatherClientNumber, [userActionTypes.CardsReadWrite]),
    cash: isAuthorised(loggedInUser, grandfatherClientNumber, [userActionTypes.CashReadWrite]) && Env.isAmpolCashEnabled(),
    reports: isAuthorised(loggedInUser, grandfatherClientNumber, [userActionTypes.ReportsReadWrite]),
    users: isAuthorised(loggedInUser, grandfatherClientNumber, [userActionTypes.UsersReadWrite]),
    maintenance: isAuthorised(loggedInUser, grandfatherClientNumber, [userActionTypes.MaintenanceReadWrite]),
    impersonate: isAuthorised(loggedInUser, grandfatherClientNumber, [userActionTypes.UsersImpersonate]),
  }));

  yield put(setPortalType(newPortalType));

  if (action?.payload?.navigate && newPortalType !== prevPortalType && action?.payload?.navigate?.location?.state !== 'globalSearch') {
    action?.payload?.navigate(newPortalType === 'cash' ? '/cash' : '/', { replace: true });
  }

  if (!isAdminPortal) {
    yield put(getAllAccountsRequest(accountNumber, grandfatherClientNumber));
  }
}

function* setUserAsAdmin() {
  const loggedInUser = yield select((state) => state.userStore.loggedInUser);

  yield put(setCanReadWrite({
    accounts: isAuthorised(loggedInUser, undefined, [userActionTypes.AccountsReadWrite]),
    cards: isAuthorised(loggedInUser, undefined, [userActionTypes.CardsReadWrite]),
    cash: isAuthorised(loggedInUser, undefined, [userActionTypes.CashReadWrite]) && Env.isAmpolCashEnabled(),
    reports: isAuthorised(loggedInUser, undefined, [userActionTypes.ReportsReadWrite]),
    users: isAuthorised(loggedInUser, undefined, [userActionTypes.UsersReadWrite]),
    maintenance: isAuthorised(loggedInUser, undefined, [userActionTypes.MaintenanceReadWrite]),
    impersonate: isAuthorised(loggedInUser, undefined, [userActionTypes.UsersImpersonate]),
  }));
}

export default function* usersSaga() {
  yield all([
    takeLatest(ActionTypes.USERS.FETCH.REQUEST, getUsers),
    takeLatest(ActionTypes.USERS.CREATE.REQUEST, createUser),
    takeLatest(ActionTypes.USERS.UPDATE.REQUEST, updateUser),
    takeLatest(ActionTypes.USERS.ADMIN_UPDATE_USER.REQUEST, adminUpdateUser),
    takeLatest(ActionTypes.USERS.UPDATE_USER_SELF.REQUEST, updateUserSelf),
    takeLatest(ActionTypes.USERS.DELETE.REQUEST, deleteUser),
    takeLatest(ActionTypes.USERS.ADMIN_DELETE_USER.REQUEST, adminDeleteUser),
    takeLatest(ActionTypes.USERS.ME.REQUEST, getUserDetails),
    takeLatest(ActionTypes.USERS.DETAILED_FETCH.REQUEST, getDetailedUser),
    takeLatest(ActionTypes.USERS.ADMIN_DETAILED_FETCH.REQUEST, adminGetDetailedUser),
    takeLatest(ActionTypes.USERS.ASSIGN_ACCOUNTS.REQUEST, assignAccountsToUser),
    takeLatest(ActionTypes.USERS.EMAIL_LOOKUP.REQUEST, getUserByEmailId),
    takeLatest(ActionTypes.USERS.DOWNLOAD.REQUEST, downloadUsers),
    takeLatest(ActionTypes.USERS.DOWNLOAD_ALL_USERS.REQUEST, downloadAllUsers),
    takeLatest(ActionTypes.USERS.SEARCH_ALL_USERS.REQUEST, getAllUsers),
    takeLatest(ActionTypes.USERS.SEARCH_ALL_IMPERSONATE_USERS.REQUEST, getAllImpersonateUsers),
    takeLatest(ActionTypes.USERS.IMPERSONATE_USER.REQUEST, impersonateUser),
    takeLatest(ActionTypes.USERS.ADMIN_GET_ASSIGNED_ACCOUNTS.REQUEST, getAssignedPortalAccountsList),
    takeLatest(ActionTypes.USERS.ADMIN_REMOVE_ACCOUNT_ACCESS.REQUEST, removeAccountAccessForUser),
    takeLatest(ActionTypes.USERS.LOGOUT.REQUEST, logout),
    takeLatest(ActionTypes.USERS.LOGIN_USER.REQUEST, loginUser),
    takeLatest(ActionTypes.USERS.SEARCH_USER_EXISTS.REQUEST, searchUserExists),
    takeLatest(ActionTypes.USERS.ADMIN_SEARCH_USER_EXISTS.REQUEST, adminSearchUserExists),
    takeLatest(ActionTypes.USERS.SET_ASSIGNED_GRANDFATHER_DETAILS, setAssignedGrandparent),
    takeLatest(ActionTypes.USERS.SET_USER_AS_ADMIN, setUserAsAdmin),
  ]);
}
